import * as React from 'react';
import HeaderMenu from 'components/Menu/HeaderMenu';
import SideMenu from 'components/Menu/SideMenu';
import { Tab } from 'components/Menu/SideMenu/SideMenu';
import { createUseStyles } from 'react-jss';
import { useRouter } from 'found';
import { createFragmentContainer, graphql } from 'react-relay';

import type { AppHolder_manager } from './__generated__/AppHolder_manager.graphql';
import ManagerSlideIn from 'components/ManagerSlideIn';
import { Logout } from 'helpers/logout';
import { getCourse } from './helpers';
import { useSideModalDispatch } from './SideModalProvider';
import Dashboard from 'components/Menu/SideMenu/Icons/Dashboard';
import Delegates from 'components/Menu/SideMenu/Icons/Delegates';
import Courses from 'components/Menu/SideMenu/Icons/Courses';
import Icon from 'sharedComponents/core/Icon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCheck, faUserCheck } from '@fortawesome/pro-regular-svg-icons';

type Props = {
  children?: React.ReactChildren;
  manager: AppHolder_manager;
  retry: () => void;
};

const useStyles = createUseStyles(() => ({
  appHolder: {
    display: 'flex',
    padding: '0px'
  },
  gridLayout: {
    display: 'grid',
    gridTemplateColumns: '256px 1fr',
    gridTemplateRows: '65px 1fr',
    height: '100vh'
  },
  holder: {
    overflow: 'scroll'
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    padding: '0px 21px',
    height: 65,
    borderBottom: '1px solid #EDEDED',
    borderRight: '1px solid #EDEDED',
    background: 'white'
  }
}));

const AppHolder = ({ children, manager, retry }: Props) => {
  const classes = useStyles();
  const { match, router } = useRouter();
  const tabs: Tab[] = [
    {
      id: 0,
      icon: Dashboard,
      name: 'Dashboard'
    },
    {
      id: 1,
      icon: Delegates,
      name: 'Delegates'
    },
    {
      id: 2,
      icon: Courses,
      name: 'Courses'
    }
  ];

  const companyTabs: Tab[] = [
    {
      id: 3,
      iconComponent: <FontAwesomeIcon icon={faFileCheck} />,
      name: 'Audits'
    },
    {
      id: 4,
      iconComponent: <FontAwesomeIcon icon={faUserCheck} />,
      name: 'Background Checks'
    }
  ];

  const [openManagerSlideIn, setOpenManagerSlideIn] = React.useState(false);
  const dispatchModal = useSideModalDispatch();

  if (manager.uuid === '00000000-0000-0000-0000-000000000000') {
    router.push('/login');
    Logout(
      () => {},
      () => {}
    );
  }

  const selected = () => {
    const { routes } = match;
    const currentRouter = routes[routes.length - 1];
    switch (currentRouter.path) {
      case '/':
        return tabs[0];
      case '/delegates':
        return tabs[1];
      case '/delegates/:uuid':
        return tabs[1];
      case '/courses':
        return tabs[2];
      case '/audits':
        return companyTabs[0];
      case '/background-checks':
        return companyTabs[1];
      default:
        return tabs[0];
    }
  };

  if (match.location.query.course) {
    console.log('open course', match.location.query.course);

    try {
      var courseID = parseInt(match.location.query.course);
      // Get course info
      dispatchModal({
        type: 'show',
        courseId: courseID
      });
    } catch (err) {
      console.warn(err);
    }
  }

  return (
    <div>
      <div className={classes.gridLayout}>
        <div className={classes.logoSection}>
          <Icon name="TTC_Logo" size={90} />
        </div>
        <HeaderMenu manager={manager} />
        <SideMenu
          manager={manager}
          onEditDetails={() => setOpenManagerSlideIn(true)}
          tabs={tabs}
          companyTabs={manager.hasComplianceAccess ? companyTabs : []}
          selected={selected()}
          onClick={(tab) => {
            console.log('tab', tab);
            switch (tab.id) {
              case 0:
                router.push('/app');
                break;
              case 1:
                router.push('/app/delegates');
                break;
              case 2:
                router.push('/app/courses');
                break;
              case 3:
                router.push('/app/audits');
                break;
              case 4:
                router.push('/app/background-checks');
                break;
              default:
                break;
            }
          }}
        />

        <div className={classes.holder}>
          <div className={classes.appHolder}>{children}</div>
        </div>
      </div>
      <ManagerSlideIn
        isOpen={openManagerSlideIn}
        onClose={() => setOpenManagerSlideIn(false)}
        refetch={retry}
        manager={manager}
      />
    </div>
  );
};

export default createFragmentContainer(AppHolder, {
  manager: graphql`
    fragment AppHolder_manager on Manager {
      uuid
      firstName
      lastName
      profileImageUrl
      hasComplianceAccess
      company {
        logoURL
        name
      }
      ...ManagerSlideIn_manager
      ...HeaderMenu_manager
    }
  `
});
